import { Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { formatNumber } from "../../../../../../../../common/utils";
import { FuelConsumptionResultsDatumDTO, FuelConsumptionResultsDTO } from "../../../../../../../../datasources/api-client/data-contracts";

export const getFuelsRows = (data: FuelConsumptionResultsDTO, t: TFunction) => {
	if (!data) return [];

	const safeNumber = (val: any): number => {
		if (val === null || val === undefined || val === "" || isNaN(Number(val))) {
			return 0;
		}
		return Number(val);
	};

	const rows = Object.entries(data.items).map(([key, value]: [string, FuelConsumptionResultsDatumDTO], index) => {
		return {
			id: index,
			type: value.fuel.label,
			usage: value.usage.label,
			consumption: {
				value: safeNumber(value?.consumption?.value.value),
				measure: value?.consumption?.value.measure,
			},
			emissions: {
				value: safeNumber(value.co2e.value),
				percent: safeNumber(value.co2e.percentage),
			},
			energy: {
				value: safeNumber(value.energy.value),
				percent: safeNumber(value.energy.percentage),
			},
		};
	});

	// Add total row
	rows.push({
		id: rows.length,
		type: t("greenrouter/total"),
		usage: "",
		consumption: {
			value: 0,
			measure: "",
		},
		emissions: {
			value: safeNumber(data.totalCo2e?.value),
			percent: 0,
		},
		energy: {
			value: safeNumber(data.totalEnergy?.value),
			percent: 0,
		},
	});

	return rows;
};

export const fuelsDataCols: GridColDef[] = [
	{
		field: "type",
		headerName: "fuel",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			const isTotal = params.row.type?.toLowerCase().includes("total");
			return (
				<Stack direction="row" alignItems="center" height="100%">
					<Typography variant="body1" fontWeight={isTotal ? 700 : 400}>
						{params.value}
					</Typography>
				</Stack>
			);
		},
	},
	{
		field: "usage",
		headerName: "usage",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			// check for the last row
			const isTotal = params.row.type?.toLowerCase().includes("total");
			return (
				<Stack spacing={2} height="100%" direction="row" justifyContent="start" alignItems="center">
					{!isTotal && <Typography variant="body1">{params.value || "-"}</Typography>}
				</Stack>
			);
		},
	},
	{
		field: "consumption",
		headerName: "consumption",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			const { value, measure } = params.row.consumption;

			// check for the last row
			const isTotal = params.row.type?.toLowerCase().includes("total");
			return (
				<Stack spacing={1} height="100%" direction="row" justifyContent="center" alignItems="center">
					{!isTotal && (
						<>
							<Typography flex={1} textAlign={"right"} variant="body1" fontWeight={700}>
								{formatNumber(value, "decimal", 2, 2)}
							</Typography>
							<Typography flex={1} variant="caption" color="text.secondary">
								{measure}
							</Typography>
						</>
					)}
				</Stack>
			);
		},
	},
	{
		field: "emissions",
		headerName: "emissions",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			const { value, percent } = params.row.emissions;
			return (
				<Stack spacing={1} height="100%" direction="row" justifyContent="center" alignItems="center">
					<Typography flex={1} textAlign={"right"} variant="body1" fontWeight={700}>
						{formatNumber(value, "decimal", 2, 2)}
					</Typography>
					<Typography flex={1} variant="caption" color="text.secondary">
						({formatNumber(percent / 100, "percent", 2, 2)})
					</Typography>
				</Stack>
			);
		},
	},
	{
		field: "energy",
		headerName: "energy",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			const { value, percent } = params.row.energy;
			return (
				<Stack spacing={1} height="100%" direction="row" justifyContent="center" alignItems="center">
					<Typography flex={1} textAlign={"right"} variant="body1" fontWeight={700}>
						{formatNumber(value, "decimal", 2, 2)}
					</Typography>
					<Typography flex={1} variant="caption" color="text.secondary">
						({formatNumber(percent / 100, "percent", 2, 2)})
					</Typography>
				</Stack>
			);
		},
	},
];

export const getFuelsColumns = (dataCol: GridColDef[], t: TFunction) =>
	dataCol.map((col, index) => {
		const label = col.headerName ? t(`greenrouter/fuel source/${col.headerName}`) : "";

		return {
			...col,
			headerName: label,
			renderHeader: () => {
				if (!label) {
					return null;
				}

				return (
					<Stack direction={"column"} width={"100%"}>
						<Stack
							direction="row"
							justifyContent={index === 0 || index === 1 ? "start" : "center"}
							sx={{ fontWeight: "bold" }}
							spacing={1}
							alignItems="baseline"
						>
							<Typography variant="subtitle1">{label}</Typography>
							<Typography variant="body2" color="text.secondary">
								{
									// measure for Emissions col
								}
								{index === 3 && "tCO2e"}

								{index === 4 && "GJ"}
							</Typography>
						</Stack>
					</Stack>
				);
			},
		};
	});
